/**
 * Panel de sincronizacion: cuan viejo es el catalogo que muestra el panel.
 *
 * Es, con diferencia, el dato mas util de esta pantalla: todo lo demas
 * (catalogo, publicables, estados) sale de `products`, o sea de lo que la
 * ultima sincronizacion dejo escrito. Si esa corrida fue hace meses, las
 * tarjetas de arriba son correctas y viejas al mismo tiempo, y eso hay que
 * decirlo arriba de los numeros y no en un pie de pagina.
 *
 * Que NO promete este panel, y por que
 * ------------------------------------
 * No dice si la ultima sincronizacion termino bien. **No se puede**: no existe
 * una tabla de corridas, nadie persiste "la sync de la tienda 1 arranco a las
 * 14:03, trajo 340 productos y fallo". El backend lo declara explicitamente con
 * `run_history_available: false` y una nota, y esta pantalla muestra esa nota
 * en vez de dibujar un panel de "ultima corrida" relleno de suposiciones.
 *
 * Lo que si se puede decir sale de dos fuentes reales:
 *
 * * `MAX(products.updated_at)` -- un **limite superior** de cuando corrio la
 *   sync, no la sync: un `PATCH /products/{id}/banned` mueve el mismo
 *   timestamp. Por eso se rotula "ultimo cambio en el catalogo".
 * * el lock distribuido de la tienda, que dice si hay una corriendo AHORA.
 */

import { daysSince, formatCount, formatDateTime, formatRelative } from "./format";
import type { StoreSyncSnapshot } from "./api";
import { Badge, Figure, Note, Panel } from "./ui";

/**
 * A partir de cuantos dias se marca el catalogo como viejo.
 *
 * Es una heuristica de la pantalla, no un dato del backend, y esta puesta en
 * una constante para que se vea que lo es. Siete dias sale de la cadencia que
 * el propio repo asume (`scripts/workflows/daily_sync.py` es diario): un
 * catalogo que no se toca en una semana significa que ese workflow no esta
 * corriendo.
 */
const STALE_AFTER_DAYS = 7;

/** Semaforo de "hay una sync corriendo", incluido el caso "no se pudo saber". */
function RunningBadge({ running }: { readonly running: boolean | null }) {
  if (running === null) {
    return (
      <Badge
        tone="muted"
        title="El backend no pudo consultar el lock distribuido (Redis no contesto). Es un dato de presentacion: se degrada a 'no se sabe' en vez de romper la pantalla. El lock de verdad, el que evita dos syncs simultaneas, sigue fallando ruidosamente del lado del servidor."
      >
        Estado del lock: no se pudo consultar
      </Badge>
    );
  }
  return running ? (
    <Badge tone="ok" title="El lock de sincronizacion de esta tienda esta tomado.">
      Sincronizacion en curso
    </Badge>
  ) : (
    <Badge tone="muted" title="El lock de sincronizacion de esta tienda esta libre.">
      Sin sincronizacion en curso
    </Badge>
  );
}

export function SyncPanel({ snapshot }: { readonly snapshot: StoreSyncSnapshot }) {
  const state = snapshot.store;
  const age = daysSince(state.last_product_change_at);
  const stale = age !== null && age > STALE_AFTER_DAYS;
  const blocked = !state.sync_enabled || !state.has_ml_tokens;

  return (
    <Panel
      title="Sincronizacion con MercadoLibre"
      lead={
        <>
          Cuando se toco por ultima vez el catalogo de esta tienda en la base, y
          si hay una corrida en curso ahora mismo.
        </>
      }
    >
      {blocked ? (
        <Note tone="danger">
          <strong>Esta tienda no puede sincronizar.</strong>{" "}
          {state.sync_enabled ? null : (
            <>
              <code>sync_enabled</code> esta en <code>false</code>.{" "}
            </>
          )}
          {state.has_ml_tokens ? null : (
            <>
              No hay <code>ml_access_token</code> guardado (se resuelve con{" "}
              <code>scripts/oauth/get_ml_authorization.py</code>).
            </>
          )}
        </Note>
      ) : null}

      <div className="home-badges">
        <RunningBadge running={state.running} />
        {stale ? (
          <Badge
            tone="warn"
            title={`Ningun producto de esta tienda cambio en mas de ${STALE_AFTER_DAYS} dias. Es un umbral de esta pantalla, no del backend.`}
          >
            Catalogo sin cambios hace mas de {STALE_AFTER_DAYS} dias
          </Badge>
        ) : null}
      </div>

      <div className="stat-grid">
        <Figure
          label="Ultimo cambio en el catalogo"
          value={
            state.last_product_change_at === null
              ? "--"
              : formatRelative(state.last_product_change_at)
          }
          tone={stale ? "warn" : "neutral"}
          hint={
            state.last_product_change_at === null
              ? "Ningun producto de esta tienda tiene fecha de actualizacion."
              : `${formatDateTime(state.last_product_change_at)} -- es MAX(products.updated_at): un limite superior de la ultima sync, no la sync.`
          }
        />
        <Figure
          label={`Productos tocados en ${state.recent_window_hours} h`}
          value={formatCount(state.products_changed_recently)}
          hint="Filas con updated_at dentro de esa ventana. Incluye cambios hechos desde el panel, no solo la sync."
        />
        <Figure
          label="Con ml_id"
          value={`${formatCount(state.products_with_ml_id)} / ${formatCount(state.products_total)}`}
          hint="Cuantos productos vinieron efectivamente de MercadoLibre."
        />
        <Figure
          label="Cambio mas viejo"
          value={
            state.oldest_product_change_at === null
              ? "--"
              : formatRelative(state.oldest_product_change_at)
          }
          hint="MIN(products.updated_at). Si estuviera muy lejos del ultimo cambio, seria la marca de una sync que se corto por la mitad."
        />
      </div>

      {snapshot.run_history_available ? null : (
        <Note tone="warn">
          <strong>Por que no dice si la ultima sincronizacion fallo:</strong>{" "}
          {snapshot.run_history_note ??
            "el backend informa que no hay historial de corridas disponible."}
        </Note>
      )}

      <Note>
        Tampoco hay boton para lanzarla desde aca:{" "}
        <code>POST /api/v1/monitoring/sync-runs</code> responde <code>501</code>{" "}
        mientras no exista la task de Celery <code>ml.sync_store_products</code>.
        Hoy se sincroniza con{" "}
        <code>python scripts/utilities/sync_all_products_from_ml.py</code>. Un
        boton que encolara igual devolveria una corrida que nadie va a ejecutar.
      </Note>

      <Note>
        Lock de esta tienda: <code>{state.lock_key}</code>. Sirve para
        inspeccionarlo a mano en Redis si una corrida quedara trabada.
      </Note>
    </Panel>
  );
}
